"use client";
import { NoSubscriptions } from "@/components/bnp-illustrations";
import { DropdownMenu, Hero, Nav } from "@/components/bnp-ui";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";
import { RealtorProfile, LoadMore, HowToListVideo } from "@/components/shared";
import SearchFilter from "@/components/shared/SearchFilter";
import { RealtorService } from "@/services";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { safeMoment } from "@/utils/safeMoment";
import parseHttpError from "@/utils/parseHttpError";

import Image from "next/image";
import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import Link from "next/link";
import useAuthToken from "@/hooks/useAuthToken";
import FooterAd from "@/components/shared/ads/FooterAd";
import { usePageAds } from "@/hooks/usePageAds";
import { AdPage, AdPlacement } from "@/types/advertising";
import { AdRenderer } from "@/components/shared/ads/AdRenderer";
import PropertyCtaBanner from "@/components/shared/PropertyCtaBanner";
import { ContentSidebar } from "@/components/layouts/Others/ContentSidebar";

interface FormData {
  search?: string;
  location?: string;
}

const Realtors = () => {
  const { user, token } = useAuthToken();
  const [allLoadedRealtors, setAllLoadedRealtors] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const router = useRouter();
  const { search, location } = router.query;
  const pageSize = 12;

  // NOTE: The backend only serves ads on the homepage for now. We fetch the
  // homepage ad set here so the placements render while testing; swap back to
  // AdPage.REALTORS once real page ads are available.
  const { inFeedAds, footerAds, headerAds, popupAds, sidebarAds } = usePageAds(
    AdPage.HOME,
  );

  // Use URL params as default values if available
  const defaultValues: FormData = {
    search: typeof search === "string" ? search : "",
    location: typeof location === "string" ? location : "",
  };

  const validationSchema = yup.object().shape({
    search: yup.string().optional(),
    location: yup.string().optional(),
  });

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });

  // Update form values when URL params change
  useEffect(() => {
    if (search) setValue("search", search as string);
    if (location) setValue("location", location as string);
  }, [search, location, setValue]);

  const onSubmit = (data: FormData) => {
    const searchParams = new URLSearchParams();

    Object.entries(data).forEach(([key, value]) => {
      if (value) {
        searchParams.append(key, value.toString());
      }
    });

    router.push({
      pathname: "/realtors",
      query: searchParams.toString(),
    });
  };

  const getRealtorsRequest = async () => {
    try {
      const queryParams: any = {
        pageNumber: currentPage,
        pageSize,
      };

      if (search) queryParams.search = search;
      if (location) queryParams.location = location;

      const response = await RealtorService.getAllRealtors(
        Object.keys(queryParams).length > 0 ? queryParams : undefined,
      );
      return response?.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const {
    data: realtorsData,
    isLoading: realtorsIsLoading,
    isError: realtorsIsError,
    refetch: realtorsRefetch,
    isSuccess: realtorsIsSuccess,
  } = useQuery({
    queryKey: ["realtors", currentPage, pageSize, search, location],
    queryFn: getRealtorsRequest,
    staleTime: 60 * 60 * 1000,
    enabled: true,
  });

  useEffect(() => {
    if (realtorsData) {
      const data = realtorsData?.data || [];
      if (currentPage === 1) {
        setAllLoadedRealtors(data);
      } else {
        setAllLoadedRealtors((prev: any[]) => [...prev, ...data]);
      }
      setTotalCount(realtorsData?.metadata?.totalCount || 0);
    }
  }, [realtorsData, currentPage]);

  const realtors = allLoadedRealtors;
  const hasMore = realtors.length < totalCount;
  const remaining = totalCount - realtors.length;

  const handleLoadMore = async () => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
  };

  useEffect(() => {
    if (currentPage > 1 && realtorsData) {
      setIsLoadingMore(false);
    }
  }, [currentPage, realtorsData]);

  return (
    <HomeWrapper
      footerSlot={footerAds[0] ? <FooterAd ad={footerAds[0]} /> : undefined}
    >
      {headerAds[0] && (
        <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
          <AdRenderer ad={headerAds[0]} placement={AdPlacement.HEADER} />
        </div>
      )}

      <ContentSidebar
        sidebarContent={
          sidebarAds?.[0] && (
            <div className="w-full mx-auto px-4 pt-4 pb-2">
              <AdRenderer ad={sidebarAds[0]} placement={AdPlacement.SIDEBAR} />
            </div>
          )
        }
        topOffset={200}
      >
        <div className={containerWidth}>
          <div className="relative w-full max-w-[1104px] mx-auto px-4 sm:px-6 lg:px-8">
            {/* Search Section */}
            <SearchFilter
              control={control}
              setValue={setValue}
              onSubmit={handleSubmit(onSubmit)}
            />

            {/* Realtors Grid */}
            <section className="py-8 md:py-12">
              <div className="mb-8">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Browse Realtors
                </h2>
                {/* <p className="text-gray-500 text-sm md:text-base">
              {realtors.length > 0
                ? `Showing ${realtors.length} trusted realtor${realtors.length !== 1 ? 's' : ''}`
                : 'No realtors found matching your criteria'}
            </p> */}
              </div>

              {realtorsIsLoading ? (
                <div className="flex flex-col justify-center items-center w-full py-20">
                  <div className="w-fit h-fit">
                    <Image
                      //@ts-ignore
                      src={"/assets/images/property-default.svg"}
                      width={200}
                      height={200}
                      alt={"loading"}
                      className="w-[200px] h-[200px] object-cover animate-pulse"
                    />
                  </div>
                </div>
              ) : (
                <>
                  {realtors.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {realtors.map((realtor: any, index: any) => (
                        <React.Fragment key={index}>
                          <div
                            className="cursor-pointer"
                            onClick={() =>
                              router.push(`/realtors/${realtor?.slug}`)
                            }
                          >
                            <RealtorProfile
                              full_name={
                                realtor?.business_name ||
                                [realtor?.first_name, realtor?.last_name]
                                  .filter(Boolean)
                                  .join(" ") ||
                                ""
                              }
                              joinedYears={
                                safeMoment(realtor?.join_date)?.fromNow() ?? "-"
                              }
                              listedProperties={
                                realtor?.listed_properties_count
                              }
                              location={
                                realtor?.business_address
                                  ? realtor.business_address
                                  : realtor?.address
                                    ? realtor.address
                                    : [realtor?.state, realtor?.city]
                                        .filter(Boolean)
                                        .join(", ")
                              }
                              whatsapp={realtor?.phone_number}
                              realtorId={realtor?.slug}
                              profile_image_url={realtor?.profile_image_url}
                            />
                          </div>
                          {/* In-feed ad after the first two rows */}
                          {index === 5 && inFeedAds[0] && (
                            <div className="col-span-full">
                              <AdRenderer
                                ad={inFeedAds[0]}
                                placement={AdPlacement.IN_FEED}
                              />
                            </div>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col justify-center items-center w-full py-20">
                      <NoSubscriptions />
                      <p className="text-grey-7 lg:w-[408px] text-center mt-4 text-lg">
                        No Realtors found
                      </p>
                    </div>
                  )}
                </>
              )}
            </section>

            <LoadMore
              hasMore={hasMore}
              isLoading={realtorsIsLoading || isLoadingMore}
              onLoadMore={handleLoadMore}
              loadedCount={realtors.length}
              totalCount={totalCount}
              pageSize={pageSize}
              className="w-full"
            />

            {/* ===== HOW TO LIST VIDEO ===== */}
            <HowToListVideo />

            {/* ===== CTA BANNER ===== */}
            <PropertyCtaBanner />
          </div>
        </div>
      </ContentSidebar>
      {popupAds[0] && (
        <AdRenderer ad={popupAds[0]} placement={AdPlacement.POPUP} />
      )}
      {footerAds[0] && (
        <div className="w-full max-w-[1540px] mx-auto my-4 px-4 lg:px-0 pt-2 pb-2">
          <AdRenderer ad={footerAds[0]} placement={AdPlacement.FOOTER} />
        </div>
      )}
    </HomeWrapper>
  );
};

export default Realtors;
